import axios from '../lib/axios';
import { toast } from 'react-toastify';
import { showToast } from '../utils/toast';
import { Permission } from '../types/auth';

const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes
const cache: Record<string, { data: any, timestamp: number }> = {};

export interface AdminUser {
  _id: string;
  name: string;
  email?: string;
  mobileNo: string;
  isSuperUser: boolean;
  suspended: boolean;
  activated: boolean;
  verified: boolean;
  dashPermissions: Permission[];
  groups?: string[]; // Add groups property
  lender?: string[]; // Lender can be a string or null
  activatedBy: string | null;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
  img?: string | null;
  role?: string;
  logs?: string[]; // Logs array
}

/**
 * Fetch all admin users
 * @param token Authentication token
 * @returns Promise with admin users data
 */
export const fetchAdminUsers = async (token: string | null): Promise<AdminUser[]> => {
  try {
    const response = await axios.get('/adminusers/admin', {
      headers: {
        'Content-Type': 'application/json',
        'token': token || ''
      }
    });
    
    if (response.data?.status === "Request successful!" && Array.isArray(response.data.result.data)) {
      return response.data.result.data;
    }
    
    throw new Error('Invalid response format from API');
  } catch (error) {
    console.error('Error fetching admin users:', error);
    showToast.error('Failed to fetch admin users');
    throw error;
  }
};

/**
 * Create a new admin user
 * @param adminData Admin user data
 * @param token Authentication token
 * @returns Promise with created admin user data
 */
export const createAdminUser = async (adminData: any, token: string | null): Promise<AdminUser> => {
  try {
    console.log('Creating admin user with data:', adminData);
    console.log('Original activated value:', adminData.activated);
    
    // Ensure proper default status values for new admins
    const adminDataWithStatus = {
      ...adminData,
      suspended: false,
      activated: true,
      verified: true
    };
    
    console.log('Sending to API with activated value:', adminDataWithStatus.activated);
    console.log('Complete request payload:', JSON.stringify(adminDataWithStatus, null, 2));
    
    const response = await axios.post('/adminusers/admin', adminDataWithStatus, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || '',
        'Authorization': token ? `Bearer ${token}` : ''
      }
    });
    
    console.log('Create admin response:', JSON.stringify(response.data, null, 2));
    console.log('Response status:', response.status);
    console.log('Activated value in response:', response.data?.result?.activated);
    
    // Check for success response in multiple ways
    if (response.status === 201 || response.status === 200) {
      // Check if we have a result object
      if (response.data?.result) {
        return response.data.result;
      } 
      // Check if we have data directly
      else if (response.data && typeof response.data === 'object') {
        return response.data;
      }
    }
    
    // Handle case where API returns success but data format is unexpected
    if (response.data?.status === "Request successful!") {
      // Try to construct a valid user object from whatever we got
      if (response.data.result) {
        return response.data.result;
      } else {
        // Create a minimal user object with the data we sent
        return {
          _id: response.data._id || `temp-${Date.now()}`,
          name: adminData.name,
          email: adminData.email || '',
          mobileNo: adminData.mobileNo,
          isSuperUser: adminData.role === 'superadmin',
          suspended: false,
          activated: true,
          verified: true,
          dashPermissions: [],
          activatedBy: null,
          lastLogin: '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
    }
    
    throw new Error('Failed to create admin user: Invalid response format');
  } catch (error: any) {
    console.error('Error creating admin user:', error);
    
    // Check if this is a duplicate mobile number error (common case)
    if (error.response?.status === 409 || 
        (error.response?.data?.message && 
         error.response.data.message.includes('mobile') && 
         error.response.data.message.includes('exist'))) {
      showToast.error('User with this mobile number already exists');
    } else {
      const errorMessage = error.response?.data?.message || 'Failed to create admin user';
      showToast.error(errorMessage);
    }
    
    throw error;
  }
};

/**
 * Update an admin user with either ID or mobile number
 * @param user The admin user to update - must contain _id and mobileNo
 * @param adminData Updated admin user data
 * @param token Authentication token
 * @returns Promise with updated admin user data
 */
export const updateAdminUser = async (user: { _id: string; mobileNo: string }, adminData: any, token: string | null): Promise<AdminUser> => {
  try {
    // Use ID for updates if we're changing mobile number, otherwise use mobile number
    const isChangingMobile = adminData.mobileNo !== user.mobileNo;
    const identifierType = isChangingMobile ? 'ID' : 'mobile number';
    const identifier = isChangingMobile ? user._id : user.mobileNo;
    
    console.log(`Updating admin user by ${identifierType}:`, identifier);
    console.log('Update data:', JSON.stringify(adminData, null, 2));
    
    // Use the correct URL pattern based on whether we're changing mobile or not
    const response = await axios.put(`/adminusers/admin/${identifier}`, adminData, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || ''
      }
    });
    
    console.log('Update admin response:', JSON.stringify(response.data, null, 2));
    
    if (response.data?.status === "Request successful!" && response.data.result?.user) {
      return response.data.result.user;
    } else if (response.data?.status === "Request successful!") {
      console.log('API returned success but with unexpected format:', response.data);
      // If API returns success but not in expected format, return a patched user object
      return {
        ...user,
        ...adminData,
        updatedAt: new Date().toISOString(),
        ...response.data.result // Add any data that might be in the result
      } as AdminUser;
    }
    
    throw new Error(`Failed to update admin user: ${response.data?.message || 'Unknown error'}`);
  } catch (error: any) {
    // Log detailed error information for debugging
    console.error('Error updating admin user:');
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    const errorMessage = error.response?.data?.message || 'Failed to update admin user';
    showToast.error(errorMessage);
    
    throw error;
  }
};

/**
 * Delete an admin user by ID or mobile number
 * @param user Admin user containing both ID and mobile number
 * @param token Authentication token
 * @returns Promise with deletion status
 */
export const deleteAdminUser = async (user: { _id: string; mobileNo: string }, token: string | null): Promise<boolean> => {
  try {
    // Try to delete by ID first as it's more reliable
    console.log(`Attempting to delete admin user with ID: ${user._id}`);
    
    const response = await axios.delete(`/adminusers/admin/${user._id}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || ''
      }
    });
    
    console.log('Delete admin response:', JSON.stringify(response.data, null, 2));
    
    if (response.data?.status === "Request successful!") {
      return true;
    }
    
    throw new Error(`Failed to delete admin user: ${response.data?.message || 'Unknown error'}`);
  } catch (error: any) {
    console.error('Error when deleting by ID, attempting with mobile number');
    
    try {
      // Try with mobile number as fallback
      console.log(`Attempting to delete admin user with mobile number: ${user.mobileNo}`);
      
      const mobileResponse = await axios.delete(`/adminusers/admin/${user.mobileNo}`, {
        headers: {
          'Content-Type': 'application/json',
          'token': token || ''
        }
      });
      
      console.log('Delete by mobile response:', JSON.stringify(mobileResponse.data, null, 2));
      
      if (mobileResponse.data?.status === "Request successful!") {
        return true;
      }
      
      throw new Error(`Failed to delete admin user: ${mobileResponse.data?.message || 'Unknown error'}`);
    } catch (mobileError: any) {
      // Log detailed error information for debugging
      console.error('Error deleting admin user:');
      if (mobileError.response) {
        console.error('Response data:', mobileError.response.data);
        console.error('Response status:', mobileError.response.status);
      } else {
        console.error('Error message:', mobileError.message);
      }
      
      const errorMessage = mobileError.response?.data?.message || 'Failed to delete admin user';
      showToast.error(errorMessage);
      
      throw mobileError;
    }
  }
};

/**
 * Fetch admin user details by mobile number
 * @param mobileNo Mobile number of the admin user
 * @param token Authentication token
 * @returns Promise with admin user data
 */
export const fetchAdminUserByMobile = async (mobileNo: string, token: string | null): Promise<AdminUser> => {
  try {
    console.log(`Fetching admin user with mobile number: ${mobileNo}`);
    
    const response = await axios.get(`/adminusers/admin/${mobileNo}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || ''
      }
    });
    
    console.log('Fetch admin user response:', JSON.stringify(response.data, null, 2));
    
    if (response.data?.status === "Request successful!" && response.data.result) {
      return response.data.result;
    }
    
    throw new Error('Invalid response format from API');
  } catch (error: any) {
    console.error('Error fetching admin user:');
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    const errorMessage = error.response?.data?.message || 'Failed to fetch admin user';
    showToast.error(errorMessage);
    
    throw error;
  }
}; 

export const getPartnersList = async (): Promise<string[]> => {
  const cacheKey = 'partners_names';
  const now = Date.now();

  // Check cache first
  if (cache[cacheKey] && (now - cache[cacheKey].timestamp) < CACHE_DURATION) {
    console.log('Returning cached partner names');
    return cache[cacheKey].data;
  }

  try {
    console.log('Fetching partner names from API');
    const response = await axios.get<{ names: string[] }>(
      `/adminusers/v1/names/list?type=partner`,
      { 
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem('token') || ''
        }
      }
    );
    
    if (response.data?.names && Array.isArray(response.data.names)) {
      const names = response.data.names
        .filter(name => name && name.trim() !== '')
        .map(name => name.trim());

      // Cache the results
      cache[cacheKey] = {
        data: names,
        timestamp: now
      };
      
      return names;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching partner names:', error);
    throw error; // Throw error to handle in component
  }
};